"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateGeometryTypes = exports.validateFeatureCollection = exports.validateFeatures = exports.validateFeature = exports.validateGeometry = exports.validateCoordinates = exports.validateCoordinatesByDepth = exports.validate = void 0;
const lodash_1 = require("lodash");
const types_1 = require("./types");
const functions_1 = require("./functions");
function transformResponse(error, data) {
    if (error) {
        return {
            valid: false,
            error,
            data,
        };
    }
    return { valid: true };
}
function validatePointsCount(coordinates, count, validateClosing = false) {
    if (!Array.isArray(coordinates))
        return false;
    const everyItemIsPoint = coordinates.every((item) => {
        if (!Array.isArray(item))
            return false;
        return item.every((c) => (0, lodash_1.isNumber)(c));
    });
    if (everyItemIsPoint) {
        const start = coordinates === null || coordinates === void 0 ? void 0 : coordinates[0];
        const end = coordinates === null || coordinates === void 0 ? void 0 : coordinates[coordinates.length - 1];
        if (validateClosing && !(0, lodash_1.isEqual)(start, end)) {
            return false;
        }
        return coordinates.length >= count;
    }
    return coordinates.every((c) => validatePointsCount(c, count, validateClosing));
}
function validate(geom) {
    const featureCollection = (0, functions_1.getFeatureCollection)(geom);
    return validateFeatureCollection(featureCollection);
}
exports.validate = validate;
function validateCoordinatesByDepth(coordinates, depth = 0) {
    if (depth === 0) {
        if (!Array.isArray(coordinates) || coordinates.length !== 2) {
            return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
                coordinates,
            });
        }
        const everyItemIsNumber = coordinates.every((c) => (0, lodash_1.isNumber)(c));
        if (!everyItemIsNumber) {
            return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
                coordinates,
            });
        }
        return transformResponse();
    }
    if (!Array.isArray(coordinates)) {
        return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
            coordinates,
        });
    }
    const invalidCoordinates = coordinates
        .map((c) => validateCoordinatesByDepth(c, depth - 1))
        .filter((i) => !i.valid);
    if (invalidCoordinates === null || invalidCoordinates === void 0 ? void 0 : invalidCoordinates.length) {
        return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
            coordinates: invalidCoordinates,
        });
    }
    return transformResponse();
}
exports.validateCoordinatesByDepth = validateCoordinatesByDepth;
function validateCoordinates(type, coordinates) {
    if (!(coordinates === null || coordinates === void 0 ? void 0 : coordinates.length)) {
        return transformResponse(types_1.ValidationError.EMPTY_COORDINATES);
    }
    if (!type) {
        return transformResponse(types_1.ValidationError.EMPTY_TYPE);
    }
    if (!Object.values(types_1.GeometryType).includes(type)) {
        return transformResponse(types_1.ValidationError.INVALID_TYPE, {
            type: type,
        });
    }
    function handleMulti(type) {
        const invalidCoordinates = coordinates
            .map((c) => validateCoordinates(type, c))
            .filter((i) => !i.valid);
        if (!(invalidCoordinates === null || invalidCoordinates === void 0 ? void 0 : invalidCoordinates.length))
            return transformResponse();
        return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
            coordinates: invalidCoordinates,
        });
    }
    if (type === types_1.GeometryType.POINT) {
        return validateCoordinatesByDepth(coordinates, 0);
    }
    else if (type === types_1.GeometryType.LINE_STRING) {
        if (!validatePointsCount(coordinates, 2)) {
            return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
                coordinates,
            });
        }
        return validateCoordinatesByDepth(coordinates, 1);
    }
    else if (type === types_1.GeometryType.POLYGON) {
        if (!validatePointsCount(coordinates, 3, true)) {
            return transformResponse(types_1.ValidationError.INVALID_COORDINATES, {
                coordinates,
            });
        }
        return validateCoordinatesByDepth(coordinates, 2);
    }
    else if (type === types_1.GeometryType.MULTI_POINT) {
        return handleMulti(types_1.GeometryType.POINT);
    }
    else if (type === types_1.GeometryType.MULTI_LINE_STRING) {
        return handleMulti(types_1.GeometryType.LINE_STRING);
    }
    else if (type === types_1.GeometryType.MULTI_POLYGON) {
        return handleMulti(types_1.GeometryType.POLYGON);
    }
}
exports.validateCoordinates = validateCoordinates;
function validateGeometry(geom) {
    if (!geom) {
        return transformResponse(types_1.ValidationError.EMTPY);
    }
    const coordinates = geom === null || geom === void 0 ? void 0 : geom.coordinates;
    if (!(coordinates === null || coordinates === void 0 ? void 0 : coordinates.length)) {
        return transformResponse(types_1.ValidationError.EMTPY);
    }
    return validateCoordinates(geom === null || geom === void 0 ? void 0 : geom.type, coordinates);
}
exports.validateGeometry = validateGeometry;
function validateFeature(feature) {
    if (feature.type !== 'Feature') {
        return transformResponse(types_1.ValidationError.INVALID_TYPE, {
            type: feature.type,
        });
    }
    else if ((feature === null || feature === void 0 ? void 0 : feature.properties) && !(0, lodash_1.isObject)(feature.properties)) {
        return transformResponse(types_1.ValidationError.INVALID_PROPERTIES, {
            properties: feature.properties,
        });
    }
    return validateGeometry(feature === null || feature === void 0 ? void 0 : feature.geometry);
}
exports.validateFeature = validateFeature;
function validateFeatures(features) {
    if (!(features === null || features === void 0 ? void 0 : features.length)) {
        return transformResponse(types_1.ValidationError.EMPTY_FEATURES);
    }
    const invalidFeatures = features === null || features === void 0 ? void 0 : features.map((feature, index) => {
        const valid = validateFeature(feature);
        return Object.assign(Object.assign({}, valid), { featureIndex: index });
    }).filter((i) => !i.valid);
    if ((invalidFeatures === null || invalidFeatures === void 0 ? void 0 : invalidFeatures.length) > 0) {
        return transformResponse(types_1.ValidationError.INVALID_FEATURES, invalidFeatures);
    }
    return transformResponse();
}
exports.validateFeatures = validateFeatures;
function validateFeatureCollection(geom) {
    var _a;
    if (!geom) {
        return transformResponse(types_1.ValidationError.EMTPY);
    }
    else if (!((_a = geom === null || geom === void 0 ? void 0 : geom.features) === null || _a === void 0 ? void 0 : _a.length)) {
        return transformResponse(types_1.ValidationError.EMPTY_FEATURES);
    }
    return validateFeatures(geom.features);
}
exports.validateFeatureCollection = validateFeatureCollection;
function validateGeometryTypes(types, geom) {
    if (!Array.isArray(types))
        types = [types];
    const everyTypeValid = types.every((t) => Object.values(types_1.GeometryType).includes(t));
    if (!everyTypeValid) {
        throw new Error(`Passed types are not valid - ["${types.join("', '")}"]`);
    }
    const invalidTypes = (0, functions_1.getGeometries)(geom)
        .map((item) => item.type)
        .filter((t) => !types.includes(t));
    if (invalidTypes === null || invalidTypes === void 0 ? void 0 : invalidTypes.length) {
        return transformResponse(types_1.ValidationError.INVALID_TYPE, {
            types: invalidTypes,
        });
    }
    return transformResponse();
}
exports.validateGeometryTypes = validateGeometryTypes;
//# sourceMappingURL=validate.js.map